import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Save, ChevronDown, ChevronUp, GraduationCap, ClipboardList,
  AlertTriangle, ListChecks, Users, FileText
} from 'lucide-react';

const PROVIDERS = [
  { key: 'slp', label: 'SLP (Speech-Language Pathologist)' },
  { key: 'school_psychologist', label: 'School Psychologist' },
  { key: 'sped_teacher', label: 'Special Education Teacher' },
  { key: 'occupational_therapist', label: 'Occupational Therapist' },
  { key: 'physical_therapist', label: 'Physical Therapist' },
  { key: 'other_provider', label: 'Other Provider(s)' },
];

const CURRICULUM_FIELDS = [
  { key: 'academic_achievement', label: 'Academic Achievement', icon: GraduationCap },
  { key: 'behavior', label: 'Behavior', icon: AlertTriangle },
  { key: 'functional_performance', label: 'Functional Performance', icon: ListChecks },
];

function buildDefaults() {
  const pp = {};
  PROVIDERS.forEach(p => { pp[p.key] = ''; });
  return {
    state_district_assessments: '',
    general_curriculum: { academic_achievement: '', behavior: '', functional_performance: '' },
    special_education: { provider_progress: pp, service_coordinator_reports: '' },
  };
}

function mergeContent(existing) {
  const defaults = buildDefaults();
  if (!existing || typeof existing !== 'object') return defaults;
  const merged = JSON.parse(JSON.stringify(defaults));
  if (existing.state_district_assessments) merged.state_district_assessments = existing.state_district_assessments;
  if (existing.general_curriculum) Object.assign(merged.general_curriculum, existing.general_curriculum);
  if (existing.special_education) {
    if (existing.special_education.provider_progress && typeof existing.special_education.provider_progress === 'object') {
      Object.assign(merged.special_education.provider_progress, existing.special_education.provider_progress);
    }
    if (existing.special_education.service_coordinator_reports) {
      merged.special_education.service_coordinator_reports = existing.special_education.service_coordinator_reports;
    }
  }
  return merged;
}

function CollapsibleGroup({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button className="flex items-center justify-between w-full mb-3 px-1" onClick={() => setOpen(!open)}>
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959]">{title}</h3>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-[#595959]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#595959]" />}
      </button>
      {open && children}
    </div>
  );
}

export default function PresentLevelsEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const handleSave = () => onSave(content);

  const update = (path, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) {
        if (!ref[path[i]]) ref[path[i]] = {};
        ref = ref[path[i]];
      }
      ref[path[path.length - 1]] = value;
      return next;
    });
  };

  return (
    <div className="max-w-4xl space-y-8" data-testid="present-levels-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="present-levels-title">Present Levels</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="present-levels-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* State & District Assessments */}
      <CollapsibleGroup title="State & District Assessments">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="pl-assessments">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList className="h-4 w-4 text-[#5A8A5A]" />
            <label className="text-sm font-medium text-[#1C1C1C]">State & District Assessment Results</label>
          </div>
          <Textarea value={content.state_district_assessments || ''} onChange={e => update(['state_district_assessments'], e.target.value)}
            placeholder="Enter state and district assessment results..." rows={4} className="rounded-xl border-[#E5E5E5] text-sm"
            data-testid="pl-assessments-input" />
        </div>
      </CollapsibleGroup>

      {/* General Curriculum Performance */}
      <CollapsibleGroup title="Current Performance in General Curriculum">
        <div className="space-y-3">
          {CURRICULUM_FIELDS.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.key} className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid={`pl-curriculum-${f.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-[#5A8A5A]" />
                  <label className="text-sm font-medium text-[#1C1C1C]">{f.label}</label>
                  {(content.general_curriculum?.[f.key] || '').trim() && <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full ml-auto">Has content</span>}
                </div>
                <Textarea value={content.general_curriculum?.[f.key] || ''} onChange={e => update(['general_curriculum', f.key], e.target.value)}
                  placeholder={`Describe current ${f.label.toLowerCase()} performance...`} rows={3} className="rounded-xl border-[#E5E5E5] text-sm"
                  data-testid={`pl-curriculum-input-${f.key}`} />
              </div>
            );
          })}
        </div>
      </CollapsibleGroup>

      {/* Special Education — Provider-Specific Progress */}
      <CollapsibleGroup title="Special Education — Progress on Goals by Provider">
        <div className="space-y-3">
          {PROVIDERS.map(prov => (
            <div key={prov.key} className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid={`pl-provider-${prov.key}`}>
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-[#5A8A5A]" />
                <label className="text-sm font-medium text-[#1C1C1C]">{prov.label}</label>
                {(content.special_education?.provider_progress?.[prov.key] || '').trim() && <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full ml-auto">Has content</span>}
              </div>
              <Textarea value={content.special_education?.provider_progress?.[prov.key] || ''} onChange={e => update(['special_education', 'provider_progress', prov.key], e.target.value)}
                placeholder={`Document progress on goals reported by ${prov.label}...`} rows={3} className="rounded-xl border-[#E5E5E5] text-sm"
                data-testid={`pl-provider-input-${prov.key}`} />
            </div>
          ))}
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="pl-svc-coordinator">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-[#5A8A5A]" />
              <label className="text-sm font-medium text-[#1C1C1C]">Service Coordinator Reports</label>
            </div>
            <Textarea value={content.special_education?.service_coordinator_reports || ''} onChange={e => update(['special_education', 'service_coordinator_reports'], e.target.value)}
              placeholder="Enter service coordinator reports..." rows={3} className="rounded-xl border-[#E5E5E5] text-sm"
              data-testid="pl-svc-coordinator-input" />
          </div>
        </div>
      </CollapsibleGroup>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="present-levels-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Present Levels'}
        </Button>
      </div>
    </div>
  );
}
